/**
 * The Apps tab's Logs for one service: the last LOG_LINES lines the runner kept, read from the Logs view's own stream
 * (`logsStreamUrl`, server-sent events) and closed at its first event (`initial`, what the runner kept), so nothing
 * stays open in the background. "Refresh" reads them again; "Open Logs" opens the live Logs view on this service.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { logsStreamUrl, sessionIdFor } from '../../../services/SandboxService';
import { LOG_LINES, logTail } from '../../../utils/serviceTabsModel';
import { Card, OpenLink } from './common';

// The runner answers at once when it knows the service; past this the tab says it got nothing.
export const LOGS_WAIT_MS = 8000;

export default function LogsTab({ projectId, serviceName, sessionId = null, onOpen = null }) {
  const [state, setState] = useState({ loading: true, lines: [], error: null });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (typeof EventSource === 'undefined') { setState({ loading: false, lines: [], error: 'This browser cannot read the log stream.' }); return undefined; }
    setState({ loading: true, lines: [], error: null });
    const source = new EventSource(logsStreamUrl(sessionId || sessionIdFor(projectId, serviceName)), { withCredentials: true });
    const done = (next) => { source.close(); clearTimeout(timer); setState({ loading: false, lines: [], error: null, ...next }); };
    const timer = setTimeout(() => done({ error: 'The runner did not answer.' }), LOGS_WAIT_MS);
    source.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      if (data.type === 'initial') { done({ lines: logTail(data, LOG_LINES) }); }
    };
    source.onerror = () => done({ error: 'The log could not be read: the service may never have run.' });
    return () => { source.close(); clearTimeout(timer); };
  }, [projectId, serviceName, sessionId, round]);

  return (
    <div className="apps-tab-body" data-testid="apps-tabpanel-logs">
      <Card link="Refresh" onLink={state.loading ? null : () => setRound((n) => n + 1)} testId="apps-logs" title={`Last ${LOG_LINES} lines`} wide>
        {state.loading ? <p className="apps-muted">Reading the log…</p> : state.error ? <p className="apps-muted">{state.error}</p> : state.lines.length === 0 ? (
          <p className="apps-muted">No output yet. Start the service to see what it prints.</p>
        ) : (
          <div aria-label="Log lines" className="apps-log" role="log">
            {state.lines.map((item, index) => <div className={`apps-log-line is-${item.type}`} key={index}>{item.line}</div>)}
          </div>
        )}
      </Card>
      <OpenLink label="Open Logs" onOpen={onOpen} testId="apps-open-logs" />
    </div>
  );
}

LogsTab.propTypes = { onOpen: PropTypes.func, projectId: PropTypes.string, serviceName: PropTypes.string.isRequired, sessionId: PropTypes.string };
